import type { Material } from '@/components/sklad/materialTypes';
import { parseQty } from '@/components/sklad/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useState } from 'react';

const INPUT =
  'w-full text-sm text-text bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

/** Založení / úprava položky katalogu drobného materiálu. */
export function MaterialItemModal({
  item,
  categories,
  suppliers,
  onSaved,
  onClose,
  onError,
}: {
  /** null = nová položka */
  item: Material | null;
  categories: string[];
  suppliers: string[];
  onSaved: (msg: string) => void;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  useEscapeKey(onClose);
  const [category, setCategory] = useState(item?.category ?? '');
  const [name, setName] = useState(item?.name ?? '');
  const [color, setColor] = useState(item?.color ?? '');
  const [size, setSize] = useState(item?.size ?? '');
  const [code, setCode] = useState(item?.code ?? '');
  const [supplier, setSupplier] = useState(item?.supplier ?? '');
  const [unit, setUnit] = useState<'ks' | 'm'>(item?.unit ?? 'ks');
  const [minQty, setMinQty] = useState(item ? String(item.min_qty) : '');
  const [stockQty, setStockQty] = useState('');
  const [note, setNote] = useState(item?.note ?? '');
  const [archived, setArchived] = useState(Boolean(item?.archived_at));
  const [saving, setSaving] = useState(false);
  // Zip z Databáze zipů — barvu a velikost drží ta, tady by je sync přepsal.
  const fromZip = Boolean(item?.zip_id);

  async function save() {
    if (!name.trim()) {
      onError('Vyplň název');
      return;
    }
    const min = minQty.trim() ? parseQty(minQty) : 0;
    const stock = stockQty.trim() ? parseQty(stockQty) : 0;
    if (!Number.isFinite(min) || min < 0 || !Number.isFinite(stock) || stock < 0) {
      onError('Minimum i stav musí být kladné číslo');
      return;
    }
    setSaving(true);
    try {
      const body = { category, name, color, size, code, supplier, unit, min_qty: min, note };
      if (item) {
        await apiFetch(`/api/material/items/${item.id}`, {
          method: 'PATCH',
          body: { ...body, archived },
        });
        onSaved('Položka upravena');
      } else {
        await apiFetch('/api/material/items', {
          method: 'POST',
          body: { ...body, stock_qty: stock },
        });
        onSaved('Položka založena');
      }
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-xl my-10 bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text pb-4 mb-4 border-b border-border">
          {item ? 'Upravit položku' : 'Nová položka materiálu'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Druh">
            <input
              list="material-categories"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              autoComplete="off"
              placeholder="Zip, Knoflík, Guma…"
              className={INPUT}
            />
            <datalist id="material-categories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
          <Field label="Název *">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Spirálový zip 5 mm dělitelný"
              className={INPUT}
            />
          </Field>
          <Field label="Barva">
            <input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              disabled={fromZip}
              placeholder="černá"
              className={`${INPUT} disabled:bg-bg disabled:text-text-muted`}
            />
          </Field>
          <Field label="Velikost / délka">
            <input
              value={size}
              onChange={(e) => setSize(e.target.value)}
              disabled={fromZip}
              placeholder="45 cm"
              className={`${INPUT} disabled:bg-bg disabled:text-text-muted`}
            />
          </Field>
          <Field label="Kód u dodavatele">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={fromZip}
              className={`${INPUT} disabled:bg-bg disabled:text-text-muted`}
            />
          </Field>
          <Field label="Obvyklý dodavatel">
            <input
              list="material-suppliers"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              autoComplete="off"
              disabled={fromZip}
              className={`${INPUT} disabled:bg-bg disabled:text-text-muted`}
            />
            <datalist id="material-suppliers">
              {suppliers.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </Field>
          <Field label="Jednotka">
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value === 'm' ? 'm' : 'ks')}
              className={INPUT}
            >
              <option value="ks">kusy (ks)</option>
              <option value="m">metry (m)</option>
            </select>
          </Field>
          <Field label="Minimální zásoba">
            <input
              value={minQty}
              onChange={(e) => setMinQty(e.target.value)}
              inputMode="decimal"
              placeholder="0 = nehlídat"
              disabled={fromZip}
              className={`${INPUT} disabled:bg-bg disabled:text-text-muted`}
            />
          </Field>
          {!item && (
            <Field label="Aktuálně skladem">
              <input
                value={stockQty}
                onChange={(e) => setStockQty(e.target.value)}
                inputMode="decimal"
                placeholder="0"
                className={INPUT}
              />
            </Field>
          )}
        </div>

        <Field label="Poznámka" className="mt-3">
          <input value={note} onChange={(e) => setNote(e.target.value)} className={INPUT} />
        </Field>

        {item && (
          <>
            {fromZip && (
              <p className="text-xs text-text-muted mt-3">
                Zip z Databáze zipů — barva, velikost, minimum, dodavatel a kód se mění tam (Zdroje
                → Databáze zipů).
              </p>
            )}
            <p className="text-xs text-text-muted mt-3">
              Stav skladu se tu nemění — na to je výdej a inventura, ať zůstane historie.
            </p>
            <label className="flex items-center gap-2 cursor-pointer select-none w-fit mt-3">
              <input
                type="checkbox"
                checked={archived}
                onChange={(e) => setArchived(e.target.checked)}
                className="w-4 h-4 rounded border-border cursor-pointer accent-primary"
              />
              <span className="text-sm text-text-secondary">
                Archivovat (už neobjednáváme, schovat ze seznamu)
              </span>
            </label>
          </>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer transition-colors disabled:opacity-50"
          >
            {saving ? 'Ukládám…' : item ? 'Uložit' : 'Založit položku'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  className = '',
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: input přichází přes children
    <label className={`block ${className}`}>
      <span className="text-sm font-medium text-text-secondary block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
